import { byId } from "@de-xima/xima-common-js-dom";
import { registerAjaxHandler } from "./ajax-handler.js";
import { scrollChatToBottom } from "../pages/chats.js";
import { initFormRecordsLayout, startFormRecordPolling } from "../pages/form-record.js";
import { initSearchbar } from "../components/searchbar.js";
import { initAccessibility } from "./accessibility.js";

const LOGO_SELECTOR = ".index-header__portal-logo img.logo-image";
const RETRY_DELAY = 150;
const MAX_RETRIES = 2;

export function onScriptLoaded() {
  if (byId("user-portal-content-rendered") !== undefined) {
    onUserPortalContentRendered();
  } else {
    document.addEventListener("user-portal:content-rendered", () => onUserPortalContentRendered());
  }

  window.addEventListener("pageshow", () => {
    ensureLogoLoaded();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      ensureLogoLoaded();
    }
  });
}

function onUserPortalContentRendered() {
  onReadyStateLoading();

  if (document.readyState === "interactive") {
    onReadyStateInteractive();
  }

  if (document.readyState === "complete") {
    onReadyStateInteractive();
    onReadyStateComplete();
  }

  document.addEventListener("readystatechange", () => {
    if (document.readyState === "interactive") {
      onReadyStateInteractive();
    }
    if (document.readyState === "complete") {
      onReadyStateComplete();
    }
  });
}

/**
 * Called as soon as our script was loaded, the document is still loading.
 */
function onReadyStateLoading() {
  registerAjaxHandler();
}

/**
 * Called when the document has finished loading and the document has been parsed but
 * sub-resources such as scripts, images, stylesheets and frames are still loading. The
 * state indicates that the DOMContentLoaded event is about to fire.
 */
function onReadyStateInteractive() {
  initFormRecordsLayout();
  startFormRecordPolling();
  initSearchbar();
  initAccessibility();
}

/**
 * The document and all sub-resources have finished loading. The state indicates that the
 * load event is about to fire.
 */
function onReadyStateComplete() {
  scrollChatToBottom();
}

/**
 * Ensures that the user portal logo is loaded and displayed correctly. If the logo image is not loaded, attempts to reload it by appending a cache buster to the image URL. Retries this process up to a maximum number of attempts with a delay between each attempt.
 * @param attempt
 */
function ensureLogoLoaded(attempt = 0) {
  const img = document.querySelector(LOGO_SELECTOR);
  if (!img || !(img instanceof HTMLImageElement)) {
    return;
  }

  if (img.complete && img.naturalWidth > 0) {
    return;
  }

  if (!img.complete) {
    if (attempt < MAX_RETRIES) {
      setTimeout(() => ensureLogoLoaded(attempt + 1), RETRY_DELAY);
    }
    return;
  }

  reloadImage(img);

  if (attempt < MAX_RETRIES) {
    setTimeout(() => ensureLogoLoaded(attempt + 1), RETRY_DELAY);
  }
}

/**
 * Reloads the given image by appending a cache buster to its source URL and updating the `src` attribute. This forces the browser to fetch the image again, which can help resolve issues with images not loading correctly due to caching.
 * @param {HTMLImageElement} img
 */
function reloadImage(img) {
  const src = img.getAttribute("src");
  if (!src) {
    return;
  }

  const busted = appendCacheBuster(src);

  requestAnimationFrame(() => {
    img.setAttribute("src", busted);
  });
}

/**
 * Appends a cache buster query parameter to the given URL. The cache buster is a timestamp that ensures the URL is unique, forcing the browser to fetch the resource again instead of using a cached version. If the URL already has a "v" query parameter, it will be replaced with the new timestamp.
 * @param {string | URL} url - The original URL to which the cache buster should be appended.
 * @returns {string} The modified URL with the cache buster appended.
 */
function appendCacheBuster(url) {
  const parsedUrl = new URL(url, document.baseURI);

  parsedUrl.searchParams.delete("v");
  parsedUrl.searchParams.set("v", String(Date.now()));

  return parsedUrl.toString();
}
